import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/essette-api';
import { LookupService } from 'app/core/services/lookup-service';
import { I18N } from 'aurelia-i18n';
import { deepClone, forceNull } from 'app/shared/lib/utilities';
import * as Core from 'app/core/models/essette-api';

const FORMAT_OPTIONS = {
    LOGIN: 'Login',
    PASSWORD: 'Password',
    DATA: 'Text'
}

@autoinject()
export class SaveCredential extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.object
    protected credential: Core.Credential = {};

    protected editedCredential?: Core.Credential;

    protected hasGlobalAccess: boolean = false;

    protected readonly: boolean = false;

    protected FORMAT_OPTIONS = FORMAT_OPTIONS;
    protected formatOptions: string[] = [FORMAT_OPTIONS.LOGIN, FORMAT_OPTIONS.PASSWORD, FORMAT_OPTIONS.DATA];
    protected selectedFormat?: string;
    protected enteredPassword?: string;
    protected enteredData?: string;

    ////****************** Element References ***************************************////

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: LookupService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
        this.checkPermissions();
    }

    ////****************** Events ***************************************************////
    protected async credentialChanged(): Promise<void> {
        this.editedCredential = this.credential?.id ? deepClone(this.credential) : {};
        // if credential is global but user does not have global access, make readonly
        this.readonly = !!this.editedCredential?.isGlobal && !this.hasGlobalAccess;
        let format = this.editedCredential?.format;
        this.selectedFormat = format ? `${format[0].toUpperCase()}${format.slice(1)}` : undefined;
    }

    ////****************** Functions ************************************************////
    private checkPermissions(): void {
        this.hasGlobalAccess = this.env.user.hasGlobalAccess;
    }

    public reset(): void {
        if(this.credential) this.editedCredential = deepClone(this.credential);
        this.enteredPassword = undefined;
        this.enteredData = undefined;
    }

    public async submit(): Promise<boolean> {
        return this.saveCredential();
    }

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    protected buildCredential(): Core.Credential {
        const credential: Core.Credential = {
            id: this.editedCredential?.id,
            code: forceNull(this.editedCredential?.code),
            format: this.selectedFormat?.toLowerCase(),
            description: this.editedCredential?.description,
            summary: forceNull(this.editedCredential?.summary),
            expirationDateTime: this.editedCredential?.expirationDateTime
        };

        switch(this.selectedFormat) {
            case FORMAT_OPTIONS.LOGIN: {
                credential.secrets = {
                    userName: this.editedCredential?.secrets?.userName,
                    password: this.enteredPassword
                }
                break;
            }
            case FORMAT_OPTIONS.PASSWORD: {
                credential.secrets = {
                    password: this.enteredPassword
                }
                break;
            }
            case FORMAT_OPTIONS.DATA: {
                credential.secrets = {
                    data: this.enteredData
                }
                break;
            }
        }

        return credential;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async saveCredential(): Promise<boolean> {
        if(!this.credential) return false;

        try {
            this.state.beginSave();

            const response = await this.service.saveCredential(this.buildCredential());
            this.toaster.standard.saved('core:credential.$', response.isSuccessful);
            return response.isSuccessful;
        } catch(error) {
            // if duplicate constraint violated, show toast
            if(error.status.errors) {
                const invalidCode = error.status.errors.some((err: any) => err.code === 'property.constraintViolated' && (err.description === 'A credential with the same Code already exists.' || err.property === 'code'));
                if(invalidCode) {
                    this.toaster.error().message(this.i18n.tr('ui:messages.duplicate.message', { what: this.editedCredential?.code })).show();
                } else {
                    this.toaster.standard.saved('core:credential.$', false);
                }
            }
            throw error;
        } finally {
            this.state.endSave();
        }
    }
}
